import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import webAuth from '../lib/auth0Client';
import { supabase } from '../lib/supabaseClient';
import { apiFetch, setTokenRenewer } from '../../../shared/lib/apiClient';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Hard session limit — force re-login after this period
const SESSION_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 2 weeks

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const renewalTimer = useRef(null);

  const fetchProfile = useCallback(async (userId) => {
    try {
      // Use API to get profile with signed avatar URL
      const res = await apiFetch('/api/user/profile');
      if (res.ok) {
        const json = await res.json();
        const p = json.profile;
        // Expose the signed URL as avatar_url for display
        if (p.avatar_signed_url) p.avatar_url = p.avatar_signed_url;
        setProfile(p);
        return;
      }
    } catch { /* fallback to supabase */ }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error) setProfile(data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await apiFetch('/api/companies');
      if (!res.ok) return;
      const data = await res.json();
      const list = data.companies || [];
      setCompanies(list);

      const storedId = localStorage.getItem('active_company_id');
      const match = list.find(c => c.companyId === storedId);
      const active = match || list[0] || null;

      if (active) {
        localStorage.setItem('active_company_id', active.companyId);
        try {
          const permRes = await apiFetch('/api/companies/my-permissions', {
            headers: { 'X-Company-ID': active.companyId }
          });
          if (permRes.ok) {
            const permData = await permRes.json();
            setActiveCompany({ ...active, permissions: permData.permissions });
          } else {
            setActiveCompany(active);
          }
        } catch {
          setActiveCompany(active);
        }
      } else {
        setActiveCompany(null);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  }, []);

  const checkSuperadmin = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/check');
      setIsSuperadmin(res.ok);
    } catch {
      setIsSuperadmin(false);
    }
  }, []);

  const isSessionExpired = () => {
    const started = localStorage.getItem('session_started_at');
    if (!started) return false;
    return new Date().getTime() - Number(started) >= SESSION_MAX_AGE_MS;
  };

  const clearSession = () => {
    if (renewalTimer.current) clearTimeout(renewalTimer.current);
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('session_started_at');
    localStorage.removeItem('active_company_id');
    setUser(null);
    setProfile(null);
    setCompanies([]);
    setActiveCompany(null);
  };

  // Use a ref to break circular dependency between renewSession and scheduleRenewal
  const renewSessionRef = useRef(null);

  const scheduleRenewal = useCallback((expiresInSeconds) => {
    if (renewalTimer.current) clearTimeout(renewalTimer.current);
    // Renew 5 minutes before expiry (or at half-life if expiry < 10 min)
    const renewInMs = Math.max((expiresInSeconds - 300) * 1000, (expiresInSeconds / 2) * 1000, 30000);
    renewalTimer.current = setTimeout(() => {
      renewSessionRef.current?.().catch((err) => {
        console.warn('Scheduled renewal failed, user will need to re-login:', err);
      });
    }, renewInMs);
  }, []);

  // Silent token renewal via Auth0 checkSession
  const renewSession = useCallback(() => {
    // Hard cutoff: no renewal after 2 weeks — force re-login
    if (isSessionExpired()) {
      clearSession();
      return Promise.reject(new Error('Session expired after 2 weeks'));
    }

    return new Promise((resolve, reject) => {
      webAuth.checkSession(
        {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
        (err, authResult) => {
          if (err) {
            console.warn('Silent token renewal failed:', err.error || err.message);
            return reject(err);
          }
          if (!authResult || !authResult.accessToken) {
            return reject(new Error('No auth result from checkSession'));
          }

          const expiresAt = JSON.stringify(
            authResult.expiresIn * 1000 + new Date().getTime()
          );
          localStorage.setItem('access_token', authResult.accessToken);
          localStorage.setItem('id_token', authResult.idToken);
          localStorage.setItem('expires_at', expiresAt);

          // Schedule next renewal 5 minutes before expiry
          scheduleRenewal(authResult.expiresIn);

          resolve(authResult);
        }
      );
    });
  }, [scheduleRenewal]);

  // Keep ref in sync
  renewSessionRef.current = renewSession;

  const switchCompany = useCallback(async (companyId) => {
    const match = companies.find(c => c.companyId === companyId);
    if (match) {
      localStorage.setItem('active_company_id', match.companyId);
      try {
        const permRes = await apiFetch('/api/companies/my-permissions', {
          headers: { 'X-Company-ID': match.companyId }
        });
        if (permRes.ok) {
          const permData = await permRes.json();
          setActiveCompany({ ...match, permissions: permData.permissions });
        } else {
          setActiveCompany(match);
        }
      } catch {
        setActiveCompany(match);
      }
    }
  }, [companies]);

  const syncUser = useCallback((userInfo, overrideName) => {
    setUser(userInfo);
    const supabaseUserId = userInfo.sub?.replace('auth0|', '');
    if (!supabaseUserId) return;

    if (overrideName) {
      // First-time signup: upsert with the name the user typed
      supabase
        .from('users')
        .upsert(
          { id: supabaseUserId, full_name: overrideName, email: userInfo.email || null },
          { onConflict: 'id' }
        )
        .then(({ error: upsertErr }) => {
          if (upsertErr) console.error('Profile upsert error:', upsertErr);
          fetchProfile(supabaseUserId);
        });
    } else {
      // Returning login / session restore: ensure user row exists, but don't overwrite full_name
      supabase
        .from('users')
        .select('id')
        .eq('id', supabaseUserId)
        .single()
        .then(({ data, error }) => {
          const fallback = (userInfo.name !== userInfo.email ? userInfo.name : null) || userInfo.email?.split('@')[0] || '';
          if (error || !data) {
            // User row doesn't exist yet — create with fallback name
            supabase
              .from('users')
              .insert({ id: supabaseUserId, full_name: fallback, email: userInfo.email || null })
              .then(({ error: insertErr }) => {
                if (insertErr) console.error('Profile insert error:', insertErr);
                fetchProfile(supabaseUserId);
              });
          } else {
            // Row exists — patch full_name and email if missing
            supabase
              .from('users')
              .update({ full_name: fallback, email: userInfo.email || null })
              .eq('id', supabaseUserId)
              .is('full_name', null)
              .then(() => fetchProfile(supabaseUserId));
          }
        });
    }
  }, [fetchProfile]);

  // Check for existing session on mount
  useEffect(() => {
    // Hard session limit — force logout after 2 weeks
    if (isSessionExpired()) {
      clearSession();
      setLoading(false);
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    const idToken = localStorage.getItem('id_token');
    const expiresAt = JSON.parse(localStorage.getItem('expires_at') || '0');
    const isExpired = new Date().getTime() >= expiresAt;

    if (accessToken && idToken) {
      // Backfill session_started_at for sessions created before this feature
      if (!localStorage.getItem('session_started_at')) {
        localStorage.setItem('session_started_at', String(new Date().getTime()));
      }

      if (isExpired) {
        // Token expired — try silent renewal before giving up
        renewSession()
          .then((authResult) => {
            const newToken = authResult.accessToken;
            webAuth.client.userInfo(newToken, async (err, userInfo) => {
              if (err) {
                localStorage.removeItem('access_token');
                localStorage.removeItem('id_token');
                localStorage.removeItem('expires_at');
                setLoading(false);
                return;
              }
              syncUser(userInfo);
              await fetchCompanies();
              checkSuperadmin();
              setLoading(false);
            });
          })
          .catch(() => {
            // Renewal failed — clear tokens, user must re-login
            localStorage.removeItem('access_token');
            localStorage.removeItem('id_token');
            localStorage.removeItem('expires_at');
            setLoading(false);
          });
      } else {
        // Token still valid — use it and schedule renewal
        const expiresInSeconds = Math.floor((expiresAt - new Date().getTime()) / 1000);
        scheduleRenewal(expiresInSeconds);

        webAuth.client.userInfo(accessToken, async (err, userInfo) => {
          if (err) {
            // Token might be invalid even though not expired — try renewal
            renewSession()
              .then((authResult) => {
                webAuth.client.userInfo(authResult.accessToken, async (infoErr, info) => {
                  if (infoErr) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('id_token');
                    localStorage.removeItem('expires_at');
                    setLoading(false);
                    return;
                  }
                  syncUser(info);
                  await fetchCompanies();
                  checkSuperadmin();
                  setLoading(false);
                });
              })
              .catch(() => {
                localStorage.removeItem('access_token');
                localStorage.removeItem('id_token');
                localStorage.removeItem('expires_at');
                setLoading(false);
              });
            return;
          }
          syncUser(userInfo);
          await fetchCompanies();
          checkSuperadmin();
          setLoading(false);
        });
      }
    } else {
      setLoading(false);
    }

    // Register renewSession so apiClient can trigger renewal on 401
    setTokenRenewer(renewSession);

    return () => {
      if (renewalTimer.current) clearTimeout(renewalTimer.current);
    };
  }, [syncUser, fetchCompanies, checkSuperadmin, renewSession, scheduleRenewal]);

  const signUp = (email, password, fullName) => {
    return new Promise((resolve, reject) => {
      webAuth.signup(
        {
          connection: 'Username-Password-Authentication',
          email,
          password,
          userMetadata: { full_name: fullName }
        },
        (err, result) => {
          if (err) return reject(err);
          resolve(result);
        }
      );
    });
  };

  const signIn = (email, password, fullName) => {
    return new Promise((resolve, reject) => {
      webAuth.client.login(
        {
          realm: 'Username-Password-Authentication',
          username: email,
          password,
          scope: 'openid profile email',
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        },
        (err, authResult) => {
          if (err) return reject(err);

          // Store tokens + session start time
          const expiresAt = JSON.stringify(
            authResult.expiresIn * 1000 + new Date().getTime()
          );
          localStorage.setItem('access_token', authResult.accessToken);
          localStorage.setItem('id_token', authResult.idToken);
          localStorage.setItem('expires_at', expiresAt);
          localStorage.setItem('session_started_at', String(new Date().getTime()));

          // Schedule silent renewal before this token expires
          scheduleRenewal(authResult.expiresIn);

          // client.login() doesn't include idTokenPayload — fetch user info
          webAuth.client.userInfo(authResult.accessToken, async (infoErr, userInfo) => {
            if (infoErr) {
              console.error('userInfo error:', infoErr);
              resolve(authResult);
              return;
            }
            syncUser(userInfo, fullName);
            await fetchCompanies();
            checkSuperadmin();
            resolve(authResult);
          });
        }
      );
    });
  };

  const handleCallback = () => {
    return new Promise((resolve, reject) => {
      webAuth.parseHash((err, authResult) => {
        if (err) return reject(err);
        if (!authResult || !authResult.accessToken) {
          return reject(new Error('No auth result'));
        }

        const expiresAt = JSON.stringify(
          authResult.expiresIn * 1000 + new Date().getTime()
        );
        localStorage.setItem('access_token', authResult.accessToken);
        localStorage.setItem('id_token', authResult.idToken);
        localStorage.setItem('expires_at', expiresAt);
        localStorage.setItem('session_started_at', String(new Date().getTime()));

        scheduleRenewal(authResult.expiresIn);

        if (authResult.idTokenPayload) {
          syncUser(authResult.idTokenPayload);
        }

        resolve(authResult);
      });
    });
  };

  const signOut = () => {
    clearSession();
  };

  const isAuthenticated = () => {
    const expiresAt = JSON.parse(localStorage.getItem('expires_at') || '0');
    return new Date().getTime() < expiresAt;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        refreshProfile: () => {
          const uid = user?.sub?.replace('auth0|', '');
          if (uid) fetchProfile(uid);
        },
        loading,
        companies,
        activeCompany,
        fetchCompanies,
        switchCompany,
        signUp,
        signIn,
        signOut,
        handleCallback,
        isAuthenticated,
        isSuperadmin
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
